import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Panel } from "@/components/app/common";
import { useMe } from "@/components/app/MeContext";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg } from "@/lib/api";
import { fmtDate, inr } from "@/lib/format";

const BUDGETS = [["freight_budget", "Freight budget"], ["loading_budget", "Loading budget"], ["unloading_budget", "Unloading budget"], ["other_handling_budget", "Other handling budget"]];
const started = (o) => (o.qty_dispatched || 0) > 0;

// Read-only strip shown in Dispatch Plan and movement context
export function LmLogisticsInfo({ ctx, testid = "lm-logistics-info" }) {
  if (!ctx) return null;
  return (
    <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700" data-testid={testid}>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        <span>Original committed delivery: <b>{fmtDate(ctx.original_commit_date) || "—"}</b></span>
        <span>Preferred transport: <b>{ctx.preferred_transport_mode || "—"}</b></span>
        {BUDGETS.map(([k, l]) => <span key={k}>{l}: <b className="font-mono">{ctx[k] == null ? "—" : inr(ctx[k])}</b></span>)}
        <span>Total logistics budget: <b className="font-mono">{ctx.total_logistics_budget ? inr(ctx.total_logistics_budget) : "—"}</b></span>
      </div>
      {ctx.dispatch_instructions && <div className="mt-1 whitespace-pre-wrap"><span className="text-slate-500">Dispatch instructions: </span>{ctx.dispatch_instructions}</div>}
    </div>
  );
}

function EditForm({ order, onDone }) {
  const { data: meta } = useFetch("/logistics/meta");
  const [f, setF] = useState(Object.fromEntries(["preferred_transport_mode", "dispatch_instructions", ...BUDGETS.map(([k]) => k)].map((k) => [k, order[k] ?? ""])));
  const [reason, setReason] = useState("");
  const save = async () => {
    const num = (v) => (v === "" || v == null ? null : Number(v));
    try {
      const r = await api.put(`/orders/${order.id}/logistics-plan`, { ...f, ...Object.fromEntries(BUDGETS.map(([k]) => [k, num(f[k])])), reason });
      toast.success(r.data.budget_change_pending ? "Budget change sent to an LM approver - the current budget stays until approved" : "Transport & budget saved");
      onDone(true);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-3" data-testid="lm-logistics-form">
      <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
        <div><Label>Preferred transport mode</Label>
          <select className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm" value={f.preferred_transport_mode} onChange={(e) => setF({ ...f, preferred_transport_mode: e.target.value })} data-testid="lm-transport-mode">
            <option value="">—</option>{(meta?.modes || []).map((x) => <option key={x}>{x}</option>)}
          </select></div>
        {BUDGETS.map(([k, l]) => <div key={k}><Label>{l} (₹)</Label><Input type="number" min="0" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`lm-${k}`} /></div>)}
      </div>
      {started(order) && <div><Label>Reason for changing the budget (needed after dispatch has started - goes to an LM approver)</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="lm-budget-reason" /></div>}
      <div><Label>Dispatch instructions</Label><Textarea rows={2} value={f.dispatch_instructions} onChange={(e) => setF({ ...f, dispatch_instructions: e.target.value })} data-testid="lm-dispatch-instructions" /></div>
      <div className="flex gap-2"><Button size="sm" onClick={save} data-testid="lm-logistics-save">Save</Button><Button size="sm" variant="outline" onClick={() => onDone(false)}>Cancel</Button></div>
    </div>
  );
}

function PendingChange({ order, reload }) {
  const { can, me } = useMe();
  const req = order.budget_change_request;
  if (!req) return null;
  const decide = async (approve) => {
    const comment = approve ? "" : window.prompt("Reason for rejecting this budget change?");
    if (!approve && !comment) return;
    try { await api.post(`/orders/${order.id}/budget-change/decide`, { approve, comment }); toast.success(approve ? "Budget change approved" : "Budget change rejected"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const pending = req.status === "pending";
  const mine = req.by?.user_id === me.user_id && !me.roles.includes("admin");
  return (
    <div className={`mt-2 rounded border px-3 py-2 text-xs ${pending ? "border-amber-300 bg-amber-50 text-amber-900" : "border-slate-200 text-slate-600"}`} data-testid="lm-budget-change">
      <b>{pending ? "Budget change waiting for approval" : `Last budget change ${req.status}`}</b> · {req.by?.name} · {fmtDate(req.at)} · {req.reason}
      <div className="mt-1 flex flex-wrap gap-x-4 font-mono">{BUDGETS.map(([k, l]) => <span key={k}>{l.replace(" budget", "")}: {inr(req.old?.[k] || 0)} → {inr(req.values?.[k] || 0)}</span>)}</div>
      {!pending && req.decided_by && <div className="mt-1">by {req.decided_by.name}{req.comment ? ` - ${req.comment}` : ""}</div>}
      {pending && can("orders.approve") && !mine && <div className="mt-2 flex gap-2"><Button size="sm" onClick={() => decide(true)} data-testid="lm-budget-approve">Approve</Button><Button size="sm" variant="outline" onClick={() => decide(false)} data-testid="lm-budget-reject">Reject</Button></div>}
    </div>
  );
}

export function LogisticsBudgetPanel({ order, reload }) {
  const { can } = useMe();
  const [edit, setEdit] = useState(false);
  const editable = (can("orders.edit") || can("orders.approve")) && !["closed", "cancelled"].includes(order.status);
  return (
    <Panel title={`Transport & logistics budget (fixed before dispatch${started(order) ? " - changes now need approval" : ""})`} className="mb-4" testid="lm-logistics-panel"
      actions={editable && !edit && <Button size="sm" variant="outline" onClick={() => setEdit(true)} data-testid="lm-logistics-edit">Edit</Button>}>
      {edit ? <EditForm order={order} onDone={(ok) => { setEdit(false); if (ok) reload(); }} /> : <LmLogisticsInfo ctx={order} />}
      <PendingChange order={order} reload={reload} />
    </Panel>
  );
}
